import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import {
  Box, Typography, Avatar, Chip, CircularProgress, Alert,
  IconButton, Tooltip, Paper, TextField, InputAdornment,
  useMediaQuery, Divider, Stack,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Snackbar,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import BusinessIcon from '@mui/icons-material/Business';
import ShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicIcon from '@mui/icons-material/Public';
import apiService from '../../services/apiServices';
import globals from '../../services/globals';
import config from '../../config/config';
import OrgWorkerDialog from './OrgWorkerDialog';

// Paletë moderne dhe e butë — tone të gjalla por jo të errëta, harmonike
// mes njëra-tjetrës dhe me kontrast të mjaftueshëm për tekstin mbi sfond të çelët.
const PALETTE = [
  '#6366F1', // indigo
  '#0EA5E9', // sky
  '#8B5CF6', // violet
  '#F59E0B', // amber
  '#10B981', // emerald
  '#F43F5E', // rose
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#14B8A6', // teal
  '#F97316', // orange
];


// Stable color per department — same dept always gets the same color,
// independent of its position in the list.
function deptColor(id) {
  return PALETTE[(id || 0) % PALETTE.length];
}

function initials(firstName, lastName) {
  return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase() || '?';
}

const LINE_COLOR_LIGHT = '#A9B6C8';
const LINE_COLOR_DARK  = 'rgba(255,255,255,0.32)';

function getLineColor(theme) {
  return theme.palette.mode === 'dark' ? LINE_COLOR_DARK : LINE_COLOR_LIGHT;
}

function matchesSearch(worker, query) {
  if (!query) return false;
  const q = query.toLowerCase();
  return (
    `${worker.firstName} ${worker.lastName}`.toLowerCase().includes(q) ||
    (worker.position || '').toLowerCase().includes(q) ||
    (worker.email || '').toLowerCase().includes(q)
  );
}


/**
 * Organigrami si pemë e vërtetë, jo si kolona gazete.
 *
 * Paraprakisht degët paketoheshin në kolona: Financa binte poshtë Teknologjisë,
 * dhe pema nuk lexohej më si pemë — ishin kuti të rradhitura njëra pas tjetrës.
 * Tani forma është ajo e organigrameve të shtypura: kompania (ose drejtori)
 * sipër, drejtuesit e degëve në një rresht të vetëm me vijë të përbashkët, dhe
 * ekipi i secilit në rrjetë dy-kolonëshe poshtë tij.
 *
 * Që të mbajë qindra punonjës pa u bërë mur kartash:
 *  - ekipi tregon vetëm `TEAM_PREVIEW` anëtarët e parë, pjesa tjetër pas «+N»;
 *  - nën-ekipet rrinë të palosura derisa t'i hapësh;
 *  - rrënjët pa asnjë vartës mblidhen në një degë të vetme, jo një degë secili;
 *  - kanavaca rrëshqet anash dhe tërhiqet me maus kur degët s'nxënë në ekran.
 */
const STUB = 14;           // lartësia e këmbëzave vertikale
const BRANCH_PAD = 14;     // gjysma e hapësirës mes dy degëve fqinje
const HEAD_W = 236;
const MEMBER_W = 160;
const COL_GAP = 10;
const TEAM_W = MEMBER_W * 2 + COL_GAP;
const TEAM_PREVIEW = 10;

// Vija horizontale e rreshtit të degëve ndërtohet nga vetë degët: secila
// vizaton gjysmën e vet, e para pret të majtën, e fundit të djathtën.
function rowSx(line) {
  return {
    display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start',
    position: 'relative', pt: `${STUB * 2}px`,
    '&::before': {
      content: '""', position: 'absolute', top: 0, left: '50%',
      width: '2px', height: `${STUB}px`, bgcolor: line, transform: 'translateX(-50%)',
    },
  };
}

function rowChildSx(line, single) {
  return {
    position: 'relative', px: `${BRANCH_PAD}px`,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    ...(single ? {} : {
      '&::after': {
        content: '""', position: 'absolute', top: `-${STUB}px`, left: 0, right: 0,
        height: '2px', bgcolor: line,
      },
      '&:first-of-type::after': { left: '50%' },
      '&:last-of-type::after':  { right: '50%' },
    }),
    '&::before': {
      content: '""', position: 'absolute', top: `-${STUB}px`, left: '50%',
      width: '2px', height: `${STUB}px`, bgcolor: line, transform: 'translateX(-50%)',
    },
  };
}

// Shiriti i ngjyrës në anën e djathtë të kartës — shenja e departamentit.
function accentSx(color, inset = 10) {
  return {
    content: '""', position: 'absolute', right: inset, top: '50%', transform: 'translateY(-50%)',
    width: 4, height: '46%', minHeight: 18, borderRadius: 2, bgcolor: color,
  };
}

function fullName(w) { return `${w.firstName || ''} ${w.lastName || ''}`.trim(); }

function ToggleChip({ color, count, collapsed, onToggle }) {
  return (
    <Box component="button" type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.2, px: 0.5, py: 0.1,
        border: 0, borderRadius: 1, cursor: 'pointer', flexShrink: 0, font: 'inherit',
        bgcolor: alpha(color, 0.14), color,
        '&:hover': { bgcolor: alpha(color, 0.26) },
      }}>
      <Typography component="span" sx={{ fontSize: '.6rem', fontWeight: 800 }}>{count}</Typography>
      {collapsed ? <ExpandMoreIcon sx={{ fontSize: 13 }} /> : <ExpandLessIcon sx={{ fontSize: 13 }} />}
    </Box>
  );
}

// ── Karta e sipërme: kompania ────────────────────────────────────────────────
function CompanyCard({ name, workerCount, deptCount }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const color = theme.palette.primary.main;
  return (
    <Paper elevation={0} sx={{
      position: 'relative', minWidth: HEAD_W + 24, borderRadius: '12px',
      display: 'flex', alignItems: 'center', gap: 1.25, pl: 1.25, pr: 3, py: 1.25,
      bgcolor: isDark ? alpha('#fff', 0.06) : '#fff',
      border: '1px solid', borderColor: 'divider',
      boxShadow: isDark ? 'none' : '0 2px 10px rgba(15,23,42,.06)',
      '&::after': accentSx(color, 12),
    }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12),
      }}>
        <BusinessIcon sx={{ fontSize: 24, color }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: '.95rem', letterSpacing: '-0.01em' }}>
          {name || 'Kompania'}
        </Typography>
        <Typography noWrap sx={{ fontSize: '.72rem', color: 'text.secondary', fontWeight: 600 }}>
          {workerCount} punonjës · {deptCount} departamente
        </Typography>
      </Box>
    </Paper>
  );
}

// ── Drejtuesi i degës: kartë e bardhë me fytyrë, emër dhe rol ────────────────
function HeadCard({ worker, color, deptName, isCurrentUser, hasKids, collapsed, onToggle, count, title, subtitle, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Paper elevation={0} onClick={onClick} sx={{
      position: 'relative', width: HEAD_W, borderRadius: '12px',
      cursor: onClick ? 'pointer' : 'inherit', transition: 'box-shadow .15s, transform .15s',
      ...(onClick ? { '&:hover': { boxShadow: `0 6px 18px ${alpha(color, 0.25)}`, transform: 'translateY(-1px)' } } : {}),
      display: 'flex', alignItems: 'center', gap: 1.1, pl: 1.1, pr: 2.75, py: 1.1,
      bgcolor: isDark ? alpha('#fff', 0.06) : '#fff',
      border: isCurrentUser ? `2px solid ${theme.palette.primary.main}` : '1px solid',
      borderColor: isCurrentUser ? 'primary.main' : 'divider',
      boxShadow: isDark ? 'none' : '0 2px 10px rgba(15,23,42,.06)',
      '&::after': accentSx(color),
    }}>
      <Avatar sx={{
        width: 44, height: 44, flexShrink: 0, fontSize: '.85rem', fontWeight: 800,
        bgcolor: alpha(color, isDark ? 0.45 : 0.16), color: isDark ? '#fff' : color,
      }}>
        {worker ? initials(worker.firstName, worker.lastName) : '–'}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: '.82rem', lineHeight: 1.25 }}>
          {title ?? fullName(worker)}{isCurrentUser ? ' · Ti' : ''}
        </Typography>
        <Typography noWrap sx={{ fontSize: '.7rem', color: 'text.secondary', fontWeight: 500, lineHeight: 1.3 }}>
          {subtitle ?? (worker?.position || '—')}
        </Typography>
        {(deptName || hasKids) && (
          <Stack direction="row" alignItems="center" sx={{ mt: 0.4, gap: 0.6, minWidth: 0 }}>
            {deptName && (
              <Typography noWrap sx={{ fontSize: '.56rem', fontWeight: 800, color, letterSpacing: '.04em', minWidth: 0 }}>
                {deptName.toUpperCase()}
              </Typography>
            )}
            {hasKids && <ToggleChip color={color} count={count} collapsed={collapsed} onToggle={onToggle} />}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

// ── Anëtari i ekipit: kartë e ulët gri, emri sipër, roli poshtë ──────────────
function MemberCard({ worker, color, deptName, isCurrentUser, hasKids, collapsed, onToggle, count, highlight, onClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Paper elevation={0} onClick={onClick} sx={{
      position: 'relative', width: '100%', minWidth: 0, borderRadius: '8px',
      cursor: onClick ? 'pointer' : 'inherit',
      pl: 1, pr: 2.25, py: 0.8,
      bgcolor: highlight ? alpha(theme.palette.warning.main, 0.16) : (isDark ? alpha('#fff', 0.06) : '#EEF2F6'),
      border: isCurrentUser ? `1.5px solid ${theme.palette.primary.main}` : '1px solid transparent',
      transition: 'background-color .15s',
      '&:hover': { bgcolor: isDark ? alpha('#fff', 0.1) : '#E3E9F0' },
      '&::after': accentSx(alpha(color, isDark ? 0.85 : 0.7), 8),
    }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '.74rem', fontWeight: 800, lineHeight: 1.25 }}>
            {fullName(worker)}{isCurrentUser ? ' · Ti' : ''}
          </Typography>
          <Typography noWrap sx={{ fontSize: '.64rem', color: 'text.secondary', fontWeight: 500 }}>
            {worker.position || '—'}
          </Typography>
        </Box>
        {hasKids && <ToggleChip color={color} count={count} collapsed={collapsed} onToggle={onToggle} />}
      </Stack>
      {deptName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.3, minWidth: 0 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          <Typography noWrap sx={{ fontSize: '.56rem', color: 'text.secondary', fontWeight: 600 }}>{deptName}</Typography>
        </Box>
      )}
    </Paper>
  );
}

/**
 * Ekipi nën një drejtues: vijë poshtë, degëzim në dy kolona, pastaj rrjeta.
 * Kur një anëtar ka vartës të vetët dhe hapet, ekipi i tij zë tërë gjerësinë
 * e rreshtit pasardhës (`dense` e mbush vendin e lënë bosh në kolonën tjetër),
 * kështu gjerësia e degës s'rritet sado thellë të shkojë hierarkia.
 */
function Team({ kids, ctx, parent, color, nested = false }) {
  const { line, showAll, setShowAll, searchQuery } = ctx;
  const single = kids.length === 1;
  const expanded = searchQuery || showAll.has(parent.id);
  const shown = expanded ? kids : kids.slice(0, TEAM_PREVIEW);
  const hidden = kids.length - shown.length;
  const cols = `calc((100% - ${COL_GAP}px) / 4)`;   // qendra e kolonës së parë

  return (
    <Box sx={{ width: nested ? '100%' : (single ? MEMBER_W : TEAM_W) }}>
      {/* Lidhësi: këmbëz poshtë, vijë horizontale mes qendrave të kolonave, dy këmbëza. */}
      <Box sx={{ position: 'relative', height: nested ? STUB : STUB * 2 }}>
        {nested ? (
          <Box sx={{ position: 'absolute', left: 12, top: 0, bottom: 0, borderLeft: `1.5px dashed ${line}` }} />
        ) : single ? (
          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: line, transform: 'translateX(-50%)' }} />
        ) : (
          <>
            <Box sx={{ position: 'absolute', left: '50%', top: 0, height: STUB, width: 2, bgcolor: line, transform: 'translateX(-50%)' }} />
            <Box sx={{ position: 'absolute', left: cols, right: cols, top: STUB - 1, height: 2, bgcolor: line }} />
            <Box sx={{ position: 'absolute', left: cols, top: STUB, bottom: 0, width: 2, bgcolor: line, transform: 'translateX(-50%)' }} />
            <Box sx={{ position: 'absolute', right: cols, top: STUB, bottom: 0, width: 2, bgcolor: line, transform: 'translateX(50%)' }} />
          </>
        )}
      </Box>
      <Box sx={{
        display: 'grid', gridTemplateColumns: single ? '1fr' : '1fr 1fr', gridAutoFlow: 'row dense',
        columnGap: `${COL_GAP}px`, rowGap: '10px',
        ...(nested ? { pl: 1, borderLeft: `2px solid ${alpha(color, 0.35)}`, ml: '11px', py: 0.5 } : {}),
      }}>
        {shown.map(k => <TeamMember key={k.id} worker={k} ctx={ctx} parentDeptId={parent.departmentId ?? null} />)}
        {hidden > 0 && (
          <Button size="small" onClick={() => setShowAll(parent.id, true)}
            sx={{ gridColumn: '1 / -1', textTransform: 'none', fontSize: '.7rem', fontWeight: 700, py: 0.4,
              bgcolor: alpha(color, 0.08), color, '&:hover': { bgcolor: alpha(color, 0.16) } }}>
            + {hidden} të tjerë
          </Button>
        )}
        {!searchQuery && showAll.has(parent.id) && kids.length > TEAM_PREVIEW && (
          <Button size="small" onClick={() => setShowAll(parent.id, false)}
            sx={{ gridColumn: '1 / -1', textTransform: 'none', fontSize: '.7rem', py: 0.2, color: 'text.secondary' }}>
            Shfaq më pak
          </Button>
        )}
      </Box>
    </Box>
  );
}

function kidsOf(ctx, id) {
  const kids = ctx.childrenBy[id] || [];
  return ctx.visibleIds ? kids.filter(k => ctx.visibleIds.has(k.id)) : kids;
}

function TeamMember({ worker, ctx, parentDeptId }) {
  const kids = kidsOf(ctx, worker.id);
  const hasKids = kids.length > 0;
  const isCollapsed = !ctx.searchQuery && ctx.collapsed.has(worker.id);
  const dept = ctx.deptById[worker.departmentId];
  const color = dept ? deptColor(dept.id) : '#94A3B8';
  // Departamenti shkruhet vetëm kur ndryshon nga i eprorit.
  const showDept = Boolean(dept) && dept.id !== parentDeptId;
  const card = (
    <MemberCard worker={worker} color={color} deptName={showDept ? dept.name : null}
      isCurrentUser={worker.id === ctx.currentWorkerId} highlight={ctx.searchQuery && matchesSearch(worker, ctx.searchQuery)}
      hasKids={hasKids} collapsed={isCollapsed} onToggle={() => ctx.toggle(worker.id)} count={kids.length}
      onClick={ctx.onSelect ? () => ctx.onSelect(worker, color) : undefined} />
  );
  if (!hasKids || isCollapsed) return card;
  return (
    <>
      {card}
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <Team kids={kids} ctx={ctx} parent={worker} color={color} nested />
      </Box>
    </>
  );
}

// Një degë: drejtuesi sipër, ekipi poshtë.
function Branch({ worker, ctx, pseudo }) {
  const kids = pseudo ? pseudo.kids : kidsOf(ctx, worker.id);
  const hasKids = kids.length > 0;
  const id = pseudo ? pseudo.id : worker.id;
  const isCollapsed = !ctx.searchQuery && ctx.collapsed.has(id);
  const dept = worker ? ctx.deptById[worker.departmentId] : null;
  const color = pseudo ? '#94A3B8' : (dept ? deptColor(dept.id) : '#94A3B8');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <HeadCard worker={worker} color={color} deptName={dept?.name}
        title={pseudo?.title} subtitle={pseudo?.subtitle}
        isCurrentUser={worker && worker.id === ctx.currentWorkerId}
        hasKids={hasKids} collapsed={isCollapsed} onToggle={() => ctx.toggle(id)} count={kids.length}
        onClick={worker && ctx.onSelect ? () => ctx.onSelect(worker, color) : undefined} />
      {hasKids && !isCollapsed && (
        <Team kids={kids} ctx={ctx} parent={pseudo ? { id, departmentId: null } : worker} color={color} />
      )}
    </Box>
  );
}

// ── Kanavaca ─────────────────────────────────────────────────────────────────
export function OrgChartCanvas({ data, companyName, currentWorkerId, searchQuery = '', zoom = 1, loading, error, onSelectWorker }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const departments = useMemo(() => data?.departments || [], [data]);
  const workers = useMemo(() => data?.workers || [], [data]);

  const deptById   = useMemo(() => { const m = {}; departments.forEach(d => { m[d.id] = d; }); return m; }, [departments]);
  const workerById = useMemo(() => { const m = {}; workers.forEach(w => { m[w.id] = w; }); return m; }, [workers]);
  const childrenBy = useMemo(() => {
    const m = {};
    workers.forEach(w => {
      const key = (w.supervisorId && workerById[w.supervisorId]) ? w.supervisorId : 'root';
      (m[key] ||= []).push(w);
    });
    Object.values(m).forEach(arr => arr.sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)));
    return m;
  }, [workers, workerById]);
  const roots = childrenBy.root || [];

  // Palosja: drejtuesit e degëve hapur, çdo nën-ekip më poshtë i palosur.
  const [collapsed, setCollapsed] = useState(() => new Set());
  useEffect(() => {
    const init = new Set();
    const walk = (id, depth) => { const kids = childrenBy[id] || []; if (kids.length && depth >= 2) init.add(id); kids.forEach(k => walk(k.id, depth + 1)); };
    // Me një drejtor të vetëm sipër, degët nisin një nivel më poshtë.
    const top = roots.length === 1 && (childrenBy[roots[0].id] || []).length ? childrenBy[roots[0].id] : roots;
    top.forEach(r => walk(r.id, 1));
    setCollapsed(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workers.length]);
  const toggle = (id) => setCollapsed(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [showAll, setShowAllState] = useState(() => new Set());
  const setShowAll = (id, on) => setShowAllState(s => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const setAll = (collapseAll) => {
    if (!collapseAll) { setCollapsed(new Set()); return; }
    const all = new Set();
    const walk = (id) => { const kids = childrenBy[id] || []; if (kids.length) all.add(id); kids.forEach(k => walk(k.id)); };
    roots.forEach(r => walk(r.id));
    all.add('__loose');
    setCollapsed(all);
    setShowAllState(new Set());
  };

  // Kërkimi: përputhjet dhe të gjithë eprorët e tyre.
  const visibleIds = useMemo(() => {
    if (!searchQuery) return null;
    const match = new Set(workers.filter(w => matchesSearch(w, searchQuery)).map(w => w.id));
    const keep = new Set(match);
    match.forEach(id => { let cur = workerById[id]; let g = 0; while (cur && cur.supervisorId && workerById[cur.supervisorId] && g++ < 50) { keep.add(cur.supervisorId); cur = workerById[cur.supervisorId]; } });
    return keep;
  }, [searchQuery, workers, workerById]);

  const legend = useMemo(() => {
    const used = new Set(workers.map(w => w.departmentId).filter(Boolean));
    return departments.filter(d => used.has(d.id) && d.code !== 'EXEC');
  }, [departments, workers]);

  // Rrëshqitja: kanavaca hapet e përqendruar te maja e pemës, dhe tërhiqet me
  // maus — me dhjetë degë krah për krah, shiriti anash s'mjafton.
  const scrollRef = useRef(null);
  const drag = useRef(null);
  const dragged = useRef(false);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
  }, [data, zoom, searchQuery, loading]);
  const onPointerDown = (e) => {
    if (e.button !== 0 || e.target.closest('button, a, input')) return;
    const el = scrollRef.current;
    dragged.current = false;
    drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false };
  };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 8) return;
    d.moved = true;
    scrollRef.current.scrollLeft = d.left - dx;
    scrollRef.current.scrollTop = d.top - dy;
  };
  // Një tërheqje mbaron me «click» mbi kartën ku lëshohet mausi; ai s'duhet
  // të hapë kartelën e punonjësit.
  const endDrag = () => { dragged.current = Boolean(drag.current?.moved); drag.current = null; };
  const onSelect = onSelectWorker
    ? (w, color) => { if (dragged.current) { dragged.current = false; return; } onSelectWorker(w, color); }
    : null;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const line = getLineColor(theme);
  const ctx = { childrenBy, deptById, collapsed, toggle, showAll, setShowAll, currentWorkerId, searchQuery, visibleIds, line, onSelect };

  const visibleRoots = visibleIds ? roots.filter(r => visibleIds.has(r.id)) : roots;
  // Një drejtor i vetëm me vartës zë vendin e kompanisë në majë.
  const leader = visibleRoots.length === 1 && kidsOf(ctx, visibleRoots[0].id).length ? visibleRoots[0] : null;
  const branchHeads = leader ? kidsOf(ctx, leader.id) : visibleRoots;
  // Rrënjët pa asnjë vartës mblidhen në një degë, jo secila në degë më vete.
  const withTeam = leader ? branchHeads : branchHeads.filter(r => kidsOf(ctx, r.id).length > 0);
  const loose = leader ? [] : branchHeads.filter(r => kidsOf(ctx, r.id).length === 0);
  const branches = [
    ...withTeam.map(w => ({ key: w.id, worker: w })),
    ...(loose.length === 1 && withTeam.length === 0 ? [{ key: loose[0].id, worker: loose[0] }] : []),
    ...(loose.length && !(loose.length === 1 && withTeam.length === 0)
      ? [{ key: '__loose', pseudo: { id: '__loose', kids: loose, title: 'Pa vijë raportimi', subtitle: `${loose.length} punonjës pa epror` } }]
      : []),
  ];
  const leaderDept = leader ? deptById[leader.departmentId] : null;

  return (
    <Box sx={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      bgcolor: isDark ? 'background.default' : '#F5F7FA',
      borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden',
    }}>
      {/* Shiriti i sipërm: palosja dhe çelësi i ngjyrave, gjithmonë në sy. */}
      {(branches.length > 0 || legend.length > 0) && (
        <Box sx={{
          flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75,
          px: 1.25, py: 0.6, borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: isDark ? alpha('#fff', 0.03) : '#fff',
        }}>
          {!searchQuery && branches.length > 0 && (
            <>
              <Button size="small" startIcon={<ExpandMoreIcon />} onClick={() => setAll(false)}
                sx={{ textTransform: 'none', fontSize: '.7rem', py: 0.1 }}>Zgjero</Button>
              <Button size="small" startIcon={<ExpandLessIcon />} onClick={() => setAll(true)}
                sx={{ textTransform: 'none', fontSize: '.7rem', py: 0.1 }}>Palos</Button>
              {legend.length > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
            </>
          )}
          {legend.map(d => (
            <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
              <Box sx={{ width: 4, height: 12, borderRadius: 2, bgcolor: deptColor(d.id), flexShrink: 0 }} />
              <Typography sx={{ fontSize: '.64rem', fontWeight: 600 }}>{d.name}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box ref={scrollRef}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerLeave={endDrag}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', cursor: 'grab', userSelect: 'none', '&:active': { cursor: 'grabbing' } }}>
        {branches.length === 0 && !leader ? (
          <Typography sx={{ mt: 3, color: 'text.disabled', fontSize: '.85rem', textAlign: 'center' }}>
            {searchQuery ? 'Asnjë përputhje' : 'Asnjë punonjës'}
          </Typography>
        ) : (
          <Box sx={{
            zoom, width: 'max-content', minWidth: `${100 / zoom}%`,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            px: 3, py: 3,
          }}>
            {leader ? (
              <HeadCard worker={leader} color={leaderDept ? deptColor(leaderDept.id) : theme.palette.primary.main}
                deptName={leaderDept?.name} isCurrentUser={leader.id === currentWorkerId}
                onClick={onSelect ? () => onSelect(leader, leaderDept ? deptColor(leaderDept.id) : theme.palette.primary.main) : undefined} />
            ) : (
              <CompanyCard name={companyName} workerCount={workers.length}
                deptCount={departments.filter(d => d.code !== 'EXEC').length} />
            )}
            {branches.length > 0 && (
              <Box sx={rowSx(line)}>
                {branches.map(b => (
                  <Box key={b.key} sx={rowChildSx(line, branches.length === 1)}>
                    <Branch worker={b.worker} pseudo={b.pseudo} ctx={ctx} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}



// ── Main OrgChart ────────────────────────────────────────────────────────────
export default function OrgChart() {
  const theme = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));   // < 600px
  const isTablet  = useMediaQuery(theme.breakpoints.between('sm', 'lg')); // 600-1200px

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState(null);   // { id, color }

  // ── Share publik ──
  const [shareOpen, setShareOpen]   = useState(false);
  const DEFAULT_SHARE_OPTIONS = { showEmail: false, showPositions: true, showManagers: true, hideEmptyDepartments: true, showUnassigned: false, showCounts: true };
  const [share, setShare]           = useState({ enabled: false, token: null, options: DEFAULT_SHARE_OPTIONS });
  const [shareBusy, setShareBusy]   = useState(false);
  const [toast, setToast]           = useState('');

  const shareOptions = share.options || DEFAULT_SHARE_OPTIONS;

  const currentUser = globals().getLogedInUser();
  const currentWorkerId = currentUser?.data?.worker_id || null;
  // Company name comes from Settings (Konfigurimet → CompanyName); fall back to
  // the logged-in user's company / config only if it isn't set there.
  const [companyName, setCompanyName] = useState(
    currentUser?.company || currentUser?.data?.company || config?.company || 'Kompania'
  );
  useEffect(() => {
    // Force a fresh fetch (true) so a stale cached settings object doesn't keep
    // showing the old/fallback name after the company name is configured.
    globals().getSettings(true)
      .then(s => { const n = (s?.CompanyName || '').trim(); if (n) setCompanyName(n); })
      .catch(() => {});
  }, []);

  /**
   * Vija e raportimit ndryshohet te dosja e punëtorit, jo këtu.
   *
   * Organigrami kishte një mënyrë «Rregullo strukturën»: kartat tërhiqeshin mbi
   * njëra-tjetrën dhe eprori ndryshonte me të rënë. Një klikim i shpërqendruar
   * — ose një tërheqje e nisur pa dashje — e rishkruante hierarkinë pa pyetur,
   * dhe e vetmja gjurmë mbetej te `audit_log`. Për një të dhënë që prek
   * miratimet, pushimet dhe raportet, kjo ishte shumë e lehtë për ta bërë
   * gabim. Ndryshimi tani bëhet te profili i punëtorit, ku shihet para se të
   * ruhet dhe ruhet me një buton.
   *
   * Rruga `PUT /api/org-chart/supervisor` mbetet në server: e përdor profili.
   */

  const publicUrl = share.token
    ? `${window.location.origin}${window.location.pathname}#/public/org-chart/${share.token}`
    : '';

  const openShare = () => {
    setShareOpen(true);
    apiService().get('org-chart/share')
      .then(res => { if (res?.success) setShare(res.data); })
      .catch(() => {});
  };

  const setSharing = (enabled, regenerate = false) => {
    setShareBusy(true);
    apiService().post('org-chart/share', { enabled, regenerate })
      .then(res => {
        if (res?.success) {
          setShare(res.data);
          if (regenerate) setToast('U gjenerua një link i ri.');
          else setToast(enabled ? 'Organigrami u bë publik.' : 'Publikimi u çaktivizua.');
        }
      })
      .catch(() => setToast('Veprimi dështoi.'))
      .finally(() => setShareBusy(false));
  };

  const updateOption = (key, value) => {
    const nextOptions = { ...shareOptions, [key]: value };
    setShare(s => ({ ...s, options: nextOptions }));   // optimist
    setShareBusy(true);
    apiService().post('org-chart/share', { enabled: true, options: nextOptions })
      .then(res => { if (res?.success) setShare(res.data); })
      .catch(() => setToast('Veprimi dështoi.'))
      .finally(() => setShareBusy(false));
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard?.writeText(publicUrl)
      .then(() => setToast('Linku u kopjua.'))
      .catch(() => setToast('Nuk u kopjua dot.'));
  };

  useEffect(() => {
    setLoading(true);
    apiService()
      .get('org-chart')
      .then(res => {
        if (res?.success) setData(res.data);
        else setError('Të dhënat nuk u ngarkuan.');
      })
      .catch(() => setError('Gabim gjatë ngarkimit të org-chart.'))
      .finally(() => setLoading(false));
    // Reflekto statusin e publikimit te butoni pa pritur hapjen e dialogut.
    apiService().get('org-chart/share')
      .then(res => { if (res?.success) setShare(res.data); })
      .catch(() => {});
  }, []);

  // Set default zoom for tablet
  useEffect(() => {
    if (isTablet) setZoom(0.75);
    else if (!isMobile) setZoom(1);
  }, [isTablet, isMobile]);

  const workers = data?.workers || [];
  const regularDepts = (data?.departments || []).filter(d => d.code !== 'EXEC');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {/* ── Header — single compact toolbar row (search wraps below on mobile) ── */}
      <Box sx={{ mb: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <AccountTreeIcon color="primary" sx={{ fontSize: { xs: 20, sm: 22 } }} />
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
          Organigrami
        </Typography>
        {!loading && (
          <Chip
            label={`${workers.length} punonjës · ${regularDepts.length} dep.`}
            size="small" variant="outlined" color="primary"
            sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
          />
        )}

        <TextField
          size="small"
          placeholder="Kërko punonjës..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ width: { xs: '100%', sm: 220 }, order: { xs: 10, sm: 0 }, ml: { sm: 1 } }}
        />

        {/* Right group: share + zoom controls */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            variant={share.enabled ? 'contained' : 'outlined'}
            color={share.enabled ? 'success' : 'primary'}
            startIcon={share.enabled ? <PublicIcon /> : <ShareIcon />}
            onClick={openShare}
            sx={{ textTransform: 'none', fontSize: { xs: '0.65rem', sm: '0.72rem' }, fontWeight: 600 }}
          >
            {share.enabled ? 'Publik' : 'Publiko'}
          </Button>

          {/* Zoom controls — shown on tablet/desktop only */}
          {!isMobile && (
            <>
              <Tooltip title="Zoom in">
                <IconButton size="small" onClick={() => setZoom(z => Math.min(z + 0.15, 2))}><ZoomInIcon /></IconButton>
              </Tooltip>
              <Tooltip title="Zoom out">
                <IconButton size="small" onClick={() => setZoom(z => Math.max(z - 0.15, 0.3))}><ZoomOutIcon /></IconButton>
              </Tooltip>
              <Tooltip title="Rivendos">
                <IconButton size="small" onClick={() => setZoom(isTablet ? 0.75 : 1)}><FitScreenIcon /></IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* ── Chart area ── */}
      <OrgChartCanvas
        data={data}
        companyName={companyName}
        currentWorkerId={currentWorkerId}
        searchQuery={searchQuery}
        zoom={zoom}
        loading={loading}
        error={error}
        onSelectWorker={(w, color) => setSelected({ id: w.id, color })}
      />

      <OrgWorkerDialog
        workerId={selected?.id}
        color={selected?.color}
        onClose={() => setSelected(null)}
        onOpenWorker={(id) => {
          const w = workers.find(x => x.id === id);
          setSelected({ id, color: w?.departmentId ? deptColor(w.departmentId) : selected?.color });
        }}
      />

      {/* ── Share dialog ── */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ShareIcon fontSize="small" color="primary" /> Publiko organigramin
        </DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={share.enabled}
                disabled={shareBusy}
                onChange={(e) => setSharing(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  {share.enabled ? 'Aktiv — i qasshëm publikisht' : 'Joaktiv'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Kushdo me linkun mund ta shohë organigramin pa u kyçur (vetëm-lexim, pa email-e).
                </Typography>
              </Box>
            }
          />

          {share.enabled && publicUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>
                Linku publik
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small" fullWidth value={publicUrl}
                  InputProps={{ readOnly: true, sx: { fontSize: '0.78rem' } }}
                  onFocus={(e) => e.target.select()}
                />
                <Tooltip title="Kopjo linkun">
                  <IconButton color="primary" onClick={copyLink}><ContentCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Hap në tab të ri">
                  <IconButton onClick={() => window.open(publicUrl, '_blank', 'noopener')}><OpenInNewIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
              <Button
                size="small" startIcon={<RefreshIcon />} disabled={shareBusy}
                onClick={() => setSharing(true, true)}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Gjenero link të ri (çaktivizon linkun e vjetër)
              </Button>

              {/* Çfarë të shfaqet publikisht */}
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}>
                Çfarë të shfaqet
              </Typography>
              <Stack>
                <FormControlLabel
                  control={<Switch size="small" checked={!!shareOptions.showPositions} disabled={shareBusy}
                    onChange={(e) => updateOption('showPositions', e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Pozicionet</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={!!shareOptions.showManagers} disabled={shareBusy}
                    onChange={(e) => updateOption('showManagers', e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Menaxherët</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={shareOptions.showCounts !== false} disabled={shareBusy}
                    onChange={(e) => updateOption('showCounts', e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Numri i punonjësve</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={!shareOptions.hideEmptyDepartments} disabled={shareBusy}
                    onChange={(e) => updateOption('hideEmptyDepartments', !e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Departamentet bosh</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={shareOptions.showUnassigned !== false} disabled={shareBusy}
                    onChange={(e) => updateOption('showUnassigned', e.target.checked)} />}
                  label={<Typography sx={{ fontSize: '0.85rem' }}>Të pacaktuarit (pa departament)</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={!!shareOptions.showEmail} disabled={shareBusy}
                    onChange={(e) => updateOption('showEmail', e.target.checked)} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem' }}>Email-et</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'warning.main' }}>
                        Kujdes: shfaq adresat publikisht
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Mbyll</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}


